import { useCallback, useId } from 'react'
import { IDEOLOGY_AXES, IDEOLOGY_MIN, IDEOLOGY_MAX } from '@/constants/ideology'
import { getPositionLabel } from '@/lib/ideology'
import { cn } from '@/lib/utils'

interface IdeologySliderProps {
  axisIndex: number
  value: number
  onChange: (value: number) => void
  partyColor?: string
  disabled?: boolean
  className?: string
}

export function IdeologySlider({
  axisIndex,
  value,
  onChange,
  partyColor,
  disabled = false,
  className,
}: IdeologySliderProps) {
  const id = useId()
  const axis = IDEOLOGY_AXES[axisIndex]
  const positionLabel = getPositionLabel(axisIndex, value)

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange(Number(e.target.value))
    },
    [onChange]
  )

  // Determine the value display color based on position
  const getValueColor = () => {
    if (value < 0) return 'text-red-600'
    if (value > 0) return 'text-blue-600'
    return 'text-muted-foreground'
  }

  return (
    <div className={cn('space-y-2', className)}>
      {/* Axis label */}
      <div className="flex items-center justify-between">
        <label
          htmlFor={id}
          className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
        >
          {axis.label}
        </label>
        <span
          className={cn(
            'text-sm font-mono tabular-nums',
            getValueColor()
          )}
        >
          {value > 0 ? '+' : ''}{value}
        </span>
      </div>

      {/* Left/Right labels */}
      <div className="flex justify-between text-xs text-muted-foreground">
        <span>{axis.leftLabel}</span>
        <span>{axis.rightLabel}</span>
      </div>

      {/* Slider container */}
      <div className="relative">
        {/* Center line indicator */}
        <div className="absolute left-1/2 top-0 bottom-0 w-px bg-border z-10 pointer-events-none" />

        {/* Range slider */}
        <input
          id={id}
          type="range"
          min={IDEOLOGY_MIN}
          max={IDEOLOGY_MAX}
          step={1}
          value={value}
          onChange={handleChange}
          disabled={disabled}
          className={cn(
            'w-full h-2 rounded-full appearance-none cursor-pointer',
            'focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2',
            'disabled:cursor-not-allowed disabled:opacity-50',
            '[&::-webkit-slider-thumb]:appearance-none',
            '[&::-webkit-slider-thumb]:w-4',
            '[&::-webkit-slider-thumb]:h-4',
            '[&::-webkit-slider-thumb]:rounded-full',
            '[&::-webkit-slider-thumb]:border-2',
            '[&::-webkit-slider-thumb]:border-background',
            '[&::-webkit-slider-thumb]:shadow-md',
            '[&::-webkit-slider-thumb]:cursor-pointer',
            '[&::-moz-range-thumb]:w-4',
            '[&::-moz-range-thumb]:h-4',
            '[&::-moz-range-thumb]:rounded-full',
            '[&::-moz-range-thumb]:border-2',
            '[&::-moz-range-thumb]:border-background',
            '[&::-moz-range-thumb]:shadow-md',
            '[&::-moz-range-thumb]:cursor-pointer',
          )}
          style={{
            background: `linear-gradient(to right,
              #ef4444 0%,
              #f87171 25%,
              #9ca3af 50%,
              #60a5fa 75%,
              #3b82f6 100%)`,
            // Custom thumb color based on party color or position
            ['--thumb-color' as string]: partyColor || (value < 0 ? '#ef4444' : value > 0 ? '#3b82f6' : '#9ca3af'),
          }}
          aria-valuemin={IDEOLOGY_MIN}
          aria-valuemax={IDEOLOGY_MAX}
          aria-valuenow={value}
          aria-valuetext={`${axis.label}: ${positionLabel} (${value})`}
          aria-label={axis.label}
        />

        <style>{`
          #${CSS.escape(id)}::-webkit-slider-thumb {
            background-color: ${partyColor || (value < 0 ? '#ef4444' : value > 0 ? '#3b82f6' : '#9ca3af')};
          }
          #${CSS.escape(id)}::-moz-range-thumb {
            background-color: ${partyColor || (value < 0 ? '#ef4444' : value > 0 ? '#3b82f6' : '#9ca3af')};
          }
        `}</style>
      </div>

      {/* Position label */}
      <div className="text-center">
        <span className="text-xs text-muted-foreground">
          {positionLabel}
        </span>
      </div>
    </div>
  )
}
